import { useToastContext } from "components/alert/ToastContext";
import { Label } from "components/input/Label";
import { isAxiosError } from "axios";
import Pessoa from "model/administracao/cadastro/Pessoa";
import { EmitirNotaFiscalSaida, ItemEmissaoNotaFiscalSaida, OpcoesEmissaoNotaFiscalSaida, PagamentoEmissaoNotaFiscalSaida, ParcelaEmissaoNotaFiscalSaida, ResultadoConsultaNotaFiscalSaida, ResultadoEmissaoNotaFiscalSaida } from "model/vendas/EmitirNotaFiscalSaida";
import PessoaEndereco from "model/fiscal/PessoaEndereco";
import { Button } from "primereact/button";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { TabPanel, TabView } from "primereact/tabview";
import { useEffect, useRef, useState, SetStateAction } from "react";
import NotaFiscalSaida from "model/vendas/NotaFiscalSaida";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import NotaFiscalSaidaService from "service/vendas/NotaFiscalSaidaService";
import NotaFiscalSaidaCalculoService from "service/vendas/NotaFiscalSaidaCalculoService";
import Cfop from "model/fiscal/Cfop";
import CfopService from "service/fiscal/CfopService";
import NotaFiscalSaidaItemModal from "./NotaFiscalSaidaItemModal";
import NotaFiscalSaidaItemService from "service/vendas/NotaFiscalSaidaItemService";

const service = new NotaFiscalSaidaService();
const calculoService = new NotaFiscalSaidaCalculoService();
const cfopService = new CfopService();
const itemService = new NotaFiscalSaidaItemService();
const moeda = (valor: number) => valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const arredondar = (valor: number) => Math.round((valor + Number.EPSILON) * 100) / 100;
const dataValida = (valor: string) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(valor)) return false;
    const data = new Date(`${valor}T00:00:00Z`);
    return !Number.isNaN(data.getTime()) && data.toISOString().slice(0, 10) === valor;
};
function mensagemErroApi(dados: unknown): string | null {
    if (typeof dados === "string") return dados.trim() || null;
    if (typeof dados !== "object" || dados === null) return null;
    const resposta = dados as Record<string, unknown>;
    const erro = resposta.error;
    const erroDetalhado = typeof erro === "object" && erro !== null
        ? erro as Record<string, unknown> : null;
    const mensagens = [resposta.detail, resposta.message, resposta.mensagem, erroDetalhado?.message, erro];
    return mensagens.find((mensagem): mensagem is string => typeof mensagem === "string" && !!mensagem.trim()) || null;
}

const novoItem = (): ItemEmissaoNotaFiscalSaida => ({
    transacaoNotaFiscal: null, camposInformados: [],
    produto: null, deposito: null, lote: null, descricaoProduto: "", codigoProdutoFiscal: "",
    unidadeComercial: "UN", quantidadeComercial: 1, valorUnitario: 0, ncm: "", cest: null,
    cfop: "", cstIcms: null, csosn: null, cstIpi: null, cstPis: null, cstCofins: null,
    aliquotaIcms: null, aliquotaIpi: null, aliquotaPis: null, aliquotaCofins: null,
    cstIbsCbs: null, classificacaoTributaria: null,
    aliquotaIbsEstadual: null, aliquotaIbsMunicipal: null, aliquotaCbs: null,
    valorDesconto: 0, valorFrete: 0, valorSeguro: 0, valorOutrasDespesas: 0,
    ean: null, origemMercadoria: null
});
const novoPagamento = (): PagamentoEmissaoNotaFiscalSaida => ({ formaPagamento: null, valorPagamento: 0, descricaoPagamento: null });
const novaNota = (): EmitirNotaFiscalSaida => ({
    unidadeOrganizacional: null, serieNotaFiscal: null, transacaoNotaFiscal: null,
    destinatario: null, enderecoDestinatario: null, representante: null, condicaoPagamento: null,
    modelo: "NFE", finalidade: "NORMAL", tipoOperacao: "SAIDA", destinoOperacao: null,
    presencaComprador: "PRESENCIAL", consumidorFinal: "NAO", indicadorIntermediador: null,
    modalidadeFrete: "SEM_TRANSPORTE", naturezaOperacao: "", informacoesComplementares: "",
    numeroFatura: null, valorOriginalFatura: null, valorDescontoFatura: null, valorLiquidoFatura: null,
    itens: [], pagamentos: [novoPagamento()], parcelas: [], volumes: [], notasReferenciadas: []
});

export default function NotaFiscalSaidaEmissao({ edicao = false }: { edicao?: boolean }) {
    const { id } = useParams();
    const [parametros] = useSearchParams();
    const idPedido = parametros.get("pedido");
    const idDuplicacao = parametros.get("duplicar");
    const duplicacao = !edicao && idDuplicacao !== null;
    const preservarFiscalInicial = useRef(false);
    const navigate = useNavigate();
    const [notaOrigem, setNotaOrigem] = useState<NotaFiscalSaida | null>(null);
    const [erroCarregamento, setErroCarregamento] = useState<string | null>(null);
    const { msgErro, msgSucesso, msgAlerta } = useToastContext();
    const [opcoes, setOpcoes] = useState<OpcoesEmissaoNotaFiscalSaida | null>(null);
    const [nota, setNotaBruta] = useState<EmitirNotaFiscalSaida>(() => ({ ...novaNota(), chaveIdempotencia: crypto.randomUUID() }));
    const setNota = (alteracao: SetStateAction<EmitirNotaFiscalSaida>) => setNotaBruta(anterior => ({ ...(typeof alteracao === "function" ? alteracao(anterior) : alteracao), chaveIdempotencia: crypto.randomUUID() }));
    const [carregando, setCarregando] = useState(true);
    const [enviando, setEnviando] = useState(false);
    const [itemEmEdicao, setItemEmEdicao] = useState<{ item: ItemEmissaoNotaFiscalSaida; indice: number | null } | null>(null);
    const [resultado, setResultado] = useState<ResultadoEmissaoNotaFiscalSaida | null>(null);
    const [erroEmissao, setErroEmissao] = useState<string | null>(null);
    const [situacao, setSituacao] = useState<ResultadoConsultaNotaFiscalSaida | null>(null);
    const [consultando, setConsultando] = useState(false);
    const [cfops, setCfops] = useState<Cfop[]>([]);
    const [carregandoCfops, setCarregandoCfops] = useState(false);
    const [carregandoParametrosFiscais, setCarregandoParametrosFiscais] = useState(false);
    const [erroParametrosFiscais, setErroParametrosFiscais] = useState(false);
    const valoresInformados = useRef(new Map<number, Partial<ItemEmissaoNotaFiscalSaida>>());
    const contextosItens = useRef(new Map<number, string>());
    const notaAtual = useRef(nota);
    notaAtual.current = nota;
    const contextoFiscal = JSON.stringify([
        nota.unidadeOrganizacional?.id, nota.transacaoNotaFiscal?.id, nota.destinatario?.id,
        nota.enderecoDestinatario?.id, nota.consumidorFinal, nota.dataEmissao, nota.finalidade,
        nota.itens.map(item => [item.produto?.codigo ?? null, item.transacaoNotaFiscal?.id ?? null, item.informarCalculoIcms51 !== false])
    ]);
    const contextoFiscalAtual = useRef(contextoFiscal);
    contextoFiscalAtual.current = contextoFiscal;

    useEffect(() => {
        if (carregando) return;
        if (preservarFiscalInicial.current) {
            preservarFiscalInicial.current = false;
            setErroParametrosFiscais(false);
            setCarregandoParametrosFiscais(false);
            return;
        }
        let ativo = true;
        const atual = notaAtual.current;
        const selecionados = atual.itens.map((item, indice) => ({ item, indice,
            contexto: itemService.contextoFiscal(item, atual)
        })).filter(({ item, indice, contexto }) => !!item.produto?.codigo && contextosItens.current.get(indice) !== contexto);
        if (!selecionados.length) {
            setCarregandoParametrosFiscais(false);
            return;
        }
        setErroParametrosFiscais(false);
        setNota(anterior => ({
            ...anterior,
            itens: anterior.itens.map((item, indice) => selecionados.some(s => s.indice === indice)
                ? itemService.limparPadroes(item, valoresInformados.current.get(indice)) : item)
        }));
        if (!atual.unidadeOrganizacional?.id || !atual.transacaoNotaFiscal?.id
            || !atual.destinatario?.id || !atual.enderecoDestinatario?.id || !selecionados.length) {
            setCarregandoParametrosFiscais(false);
            return;
        }
        setCarregandoParametrosFiscais(true);
        const timer = window.setTimeout(() => {
            // Consulta os padrões; aplica a edição mais recente ao receber a resposta.
            service.consultarParametrosFiscais({ ...atual, itens: selecionados.map(({ item, indice }) =>
                itemService.entradaConsulta(item, valoresInformados.current.get(indice))) })
                .then(parametros => {
                    if (!ativo || contextoFiscalAtual.current !== contextoFiscal) return;
                    selecionados.forEach(({ indice, contexto }) => contextosItens.current.set(indice, contexto));
                    setNota(anterior => ({
                        ...anterior,
                        itens: anterior.itens.map((item, indice) => {
                            const posicao = selecionados.findIndex(selecionado => selecionado.indice === indice);
                            if (posicao < 0) return item;
                            const fiscal = parametros[posicao];
                            return itemService.aplicarParametros(item, fiscal, valoresInformados.current.get(indice));
                        })
                    }));
                })
                .catch((error: unknown) => {
                    if (!ativo || contextoFiscalAtual.current !== contextoFiscal) return;
                    setErroParametrosFiscais(true);
                    const detalhe = isAxiosError(error) ? mensagemErroApi(error.response?.data) : null;
                    msgErro("Parâmetros fiscais", detalhe || "Não foi possível selecionar os impostos para os produtos.");
                })
                .finally(() => {
                    if (ativo && contextoFiscalAtual.current === contextoFiscal)
                        setCarregandoParametrosFiscais(false);
                });
        }, 250);
        return () => {
            ativo = false;
            window.clearTimeout(timer);
        };
        // Campos tributários e valores digitados não alteram o contexto de seleção das regras fiscais.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contextoFiscal, carregando]);

    useEffect(() => {
        let ativo = true;
        setCfops([]);
        setCarregandoCfops(true);
        cfopService.listar(nota.tipoOperacao, nota.destinoOperacao)
            .then(dados => { if (ativo) setCfops(dados); })
            .catch(() => { if (ativo) msgErro("CFOP", "Não foi possível carregar o catálogo de CFOPs."); })
            .finally(() => { if (ativo) setCarregandoCfops(false); });
        return () => { ativo = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nota.tipoOperacao, nota.destinoOperacao]);

    useEffect(() => {
        let ativo = true;
        setCarregando(true);
        setErroCarregamento(null);
        setNotaOrigem(null);
        setResultado(null);
        setSituacao(null);
        setErroEmissao(null);
        async function carregar() {
            try {
                const numeroId = Number(duplicacao ? idDuplicacao : id);
                if ((edicao || duplicacao) && (!Number.isSafeInteger(numeroId) || numeroId <= 0)) {
                    throw new Error("Identificador da nota fiscal inválido.");
                }
                const [cadastros, dados, origem, pedido] = await Promise.all([
                    service.opcoesEmissao(),
                    edicao ? service.buscarEdicao(numeroId) : duplicacao ? service.prepararDuplicacao(numeroId) : Promise.resolve(null),
                    edicao ? service.consultar(numeroId) : Promise.resolve(null),
                    !edicao && !duplicacao && idPedido ? service.prepararPedido(Number(idPedido)) : Promise.resolve(null)
                ]);
                if (!ativo) return;
                setOpcoes(cadastros);
                setNotaOrigem(origem);
                valoresInformados.current = new Map();
                contextosItens.current = new Map();
                dados?.itens.forEach((item, indice) => {
                    valoresInformados.current.set(indice, itemService.valoresInformados(item));
                    contextosItens.current.set(indice, itemService.contextoFiscal(item, dados));
                });
                if (pedido) {
                    const itens = pedido.itens.filter(item => (item.qtDisponivelFaturamento ?? 0) > 0
                        && item.status !== "LIQUIDADO" && item.status !== "CANCELADO");
                    if (!itens.length) throw new Error("O pedido não possui saldo disponível para faturamento.");
                    const cliente = await service.consultarCliente(pedido.cliente.id);
                    if (!ativo) return;
                    const enderecos = cliente.enderecos?.filter(e => e.ativo) ?? [];
                    setNota({
                        ...novaNota(),
                        unidadeOrganizacional: pedido.unidadeOrganizacional ?? null,
                        destinatario: cliente, enderecoDestinatario: enderecos.find(e => e.principal) ?? enderecos[0] ?? null,
                        representante: pedido.representante ?? null, condicaoPagamento: pedido.condicaoPagamento ?? null,
                        consumidorFinal: cliente.consumidorFinalPadrao ? "SIM" : "NAO",
                        pagamentos: [{ formaPagamento: pedido.formaPagamento ?? null, valorPagamento: 0, descricaoPagamento: null }],
                        itens: itens.map(item => ({
                            ...novoItem(), itemPedido: item, produto: item.produto,
                            quantidadeComercial: item.qtDisponivelFaturamento!, valorUnitario: item.precoUnitario,
                            codigoProdutoFiscal: item.produto.codigo ?? "", descricaoProduto: item.produto.nome ?? "",
                            unidadeComercial: item.produto.unidadeMedida ?? "UN", ncm: item.produto.classificacaoFiscal?.ncm ?? "",
                            cest: item.produto.classificacaoFiscal?.cest ?? null, ean: item.produto.ean ?? null,
                            origemMercadoria: item.produto.origemMercadoria ?? null,
                            cstIpi: item.produto.classificacaoFiscal?.cstIpiPadrao ?? null,
                            cstPis: item.produto.classificacaoFiscal?.cstPisPadrao ?? null,
                            cstCofins: item.produto.classificacaoFiscal?.cstCofinsPadrao ?? null,
                            cstIbsCbs: item.produto.classificacaoFiscal?.cstIbsCbsPadrao ?? null,
                            aliquotaIbsEstadual: null, aliquotaIbsMunicipal: null, aliquotaCbs: null,
                            classificacaoTributaria: item.produto.classificacaoFiscal?.classificacaoTributariaPadrao ?? null
                        }))
                    });
                    return;
                }
                preservarFiscalInicial.current = dados !== null;
                setNota(dados ? {
                    ...novaNota(), ...dados,
                    informacoesComplementares: dados.informacoesComplementares ?? ""
                } : novaNota());
            } catch (error: unknown) {
                if (!ativo) return;
                const mensagem = isAxiosError(error) ? mensagemErroApi(error.response?.data) : null;
                const detalhe = mensagem || (error instanceof Error ? error.message : null) || "Não foi possível carregar a nota e os cadastros. Verifique se ela pode ser editada.";
                setErroCarregamento(detalhe);
                msgErro(edicao ? "Edição da nota fiscal" : "Emissão", detalhe);
            } finally {
                if (ativo) setCarregando(false);
            }
        }
        void carregar();
        return () => { ativo = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, edicao, idPedido, idDuplicacao, duplicacao]);
    const alterar = (campos: Partial<EmitirNotaFiscalSaida>) => setNota(anterior => ({ ...anterior, ...campos }));
    const alterarPagamento = (indice: number, campos: Partial<PagamentoEmissaoNotaFiscalSaida>) => setNota(anterior => ({
        ...anterior, pagamentos: anterior.pagamentos.map((item, posicao) => posicao === indice ? { ...item, ...campos } : item)
    }));
    const alterarParcela = (indice: number, campos: Partial<ParcelaEmissaoNotaFiscalSaida>) => setNota(anterior => ({
        ...anterior, parcelas: anterior.parcelas.map((parcela, posicao) => posicao === indice ? { ...parcela, ...campos } : parcela)
    }));
    const totalItem = (item: ItemEmissaoNotaFiscalSaida) => calculoService.calcularTotalItem(item, nota.transacaoNotaFiscal);
    const totalNota = arredondar(nota.itens.reduce((valor, item) => valor + totalItem(item), 0));
    const totalPagamentos = arredondar(nota.pagamentos.reduce((valor, item) => valor + item.valorPagamento, 0));
    const pagamentoAVista = calculoService.pagamentoAVista(nota.condicaoPagamento);

    function gerarCobranca() {
        const regras = nota.condicaoPagamento?.parcelas ?? [];
        if (!regras.length || totalNota <= 0) {
            msgAlerta("Condição de pagamento", "Selecione uma condição com parcelas e preencha os itens da nota.");
            return;
        }
        const base = totalNota;
        const totalPercentual = regras.reduce((valor, regra) => valor + regra.percentualRateio, 0);
        if (Math.abs(totalPercentual - 100) > 0.01) {
            msgAlerta("Condição de pagamento", "O rateio da condição deve totalizar 100%.");
            return;
        }
        const geradas: EmitirNotaFiscalSaida["parcelas"] = [];
        const formas: EmitirNotaFiscalSaida["pagamentos"] = [];
        regras.forEach(regra => {
            const quantidade = Math.max(1, regra.quantidadeParcelas);
            const valorGrupo = arredondar(base * regra.percentualRateio / 100);
            const valorBase = arredondar(valorGrupo / quantidade);
            formas.push({
                formaPagamento: regra.formaPagamento,
                valorPagamento: valorGrupo, descricaoPagamento: null
            });
            for (let indice = 0; !pagamentoAVista && indice < quantidade; indice++) {
                const data = new Date();
                data.setHours(12, 0, 0, 0);
                data.setDate(data.getDate() + regra.diasPrimeiroVencimento + indice * regra.diasEntreParcelas);
                if (regra.diaFixoMes) {
                    const dia = Math.min(regra.diaFixoMes, new Date(data.getFullYear(), data.getMonth() + 1, 0).getDate());
                    if (dia < data.getDate()) data.setMonth(data.getMonth() + 1, 1);
                    data.setDate(Math.min(regra.diaFixoMes, new Date(data.getFullYear(), data.getMonth() + 1, 0).getDate()));
                }
                const vencimento = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, "0")}-${String(data.getDate()).padStart(2, "0")}`;
                geradas.push({
                    numeroParcela: String(geradas.length + 1).padStart(3, "0"),
                    dataVencimento: vencimento, valorParcela: indice === quantidade - 1
                        ? arredondar(valorGrupo - valorBase * (quantidade - 1)) : valorBase
                });
            }
        });
        if (geradas.length) geradas[geradas.length - 1].valorParcela = arredondar(
            geradas[geradas.length - 1].valorParcela + base - geradas.reduce((v, p) => v + p.valorParcela, 0));
        if (formas.length) formas[formas.length - 1].valorPagamento = arredondar(
            formas[formas.length - 1].valorPagamento + base - formas.reduce((v, p) => v + p.valorPagamento, 0));
        alterar({
            parcelas: geradas, pagamentos: formas,
            valorOriginalFatura: totalNota,
            valorDescontoFatura: arredondar(totalNota - base),
            valorLiquidoFatura: base
        });
    }

    async function escolherCliente(cliente: Pessoa | null) {
        alterar({ destinatario: cliente, enderecoDestinatario: null });
        if (!cliente?.id) return;
        try {
            const completo = await service.consultarCliente(cliente.id);
            const enderecos = completo.enderecos?.filter(endereco => endereco.ativo) ?? [];
            setNota(anterior => anterior.destinatario?.id !== cliente.id ? anterior : ({
                ...anterior, destinatario: completo,
                enderecoDestinatario: enderecos.find(endereco => endereco.principal) ?? enderecos[0] ?? null,
                consumidorFinal: completo.consumidorFinalPadrao ? "SIM" : "NAO"
            }));
        } catch {
            msgErro("Destinatário", "Não foi possível carregar os endereços do cliente.");
        }
    }

    function validar(): boolean {
        if (!nota.unidadeOrganizacional || !nota.serieNotaFiscal || !nota.transacaoNotaFiscal
            || !nota.destinatario || !nota.enderecoDestinatario || !nota.naturezaOperacao.trim()) {
            msgAlerta("Dados obrigatórios", "Informe unidade, série, transação, cliente, endereço e natureza da operação.");
            return false;
        }
        if (nota.serieNotaFiscal.unidadeOrganizacional?.id !== nota.unidadeOrganizacional.id) {
            msgAlerta("Série", "A série selecionada não pertence à unidade organizacional.");
            return false;
        }
        if (nota.destinoOperacao === "EXTERIOR" || nota.enderecoDestinatario.uf === "EX") {
            msgAlerta("Exportação", "Esta rota ainda não contempla os dados necessários para emissão ao exterior.");
            return false;
        }
        if (!nota.itens.length || nota.itens.some(item => !item.produto?.codigo || !item.ncm
            || !item.cfop || !item.unidadeComercial || item.quantidadeComercial <= 0
            || item.valorUnitario <= 0 || totalItem(item) <= 0)) {
            msgAlerta("Itens", "Confira produto, NCM, CFOP, unidade, quantidade e valor de cada item.");
            return false;
        }
        if (nota.itens.some(item => itemService.transacao(item, nota)?.movimentaEstoque && (!item.deposito?.codigo || !item.lote))) {
            msgAlerta("Estoque", "Informe depósito e lote de cada item para a transação que movimenta estoque.");
            return false;
        }
        if (nota.itens.some(item => item.transacaoNotaFiscal && !itemService.transacaoCompativel(item.transacaoNotaFiscal, nota))) {
            msgAlerta("Itens", "Confira a finalidade e o destino das transações dos itens.");
            return false;
        }
        if (carregandoCfops || nota.itens.some(item => !cfops.some(cfop => cfop.codigo === item.cfop))) {
            msgAlerta("CFOP", "Selecione CFOPs do catálogo compatíveis com a operação.");
            return false;
        }
        for (let indice = 0; indice < nota.itens.length; indice++) {
            const item = nota.itens[indice];
            const possuiIbsCbs = item.cstIbsCbs != null || item.classificacaoTributaria != null
                || item.aliquotaIbsEstadual != null || item.aliquotaIbsMunicipal != null || item.aliquotaCbs != null;
            if (!possuiIbsCbs) continue;
            if (!/^\d{3}$/.test(item.cstIbsCbs ?? "") || !/^\d{6}$/.test(item.classificacaoTributaria ?? "")) {
                msgAlerta("IBS/CBS", `Informe CST (3 dígitos) e classificação tributária (6 dígitos) no item ${indice + 1}.`);
                return false;
            }
            const aliquotas = [item.aliquotaIbsEstadual, item.aliquotaIbsMunicipal, item.aliquotaCbs];
            if (aliquotas.some(aliquota => aliquota != null && (!Number.isFinite(aliquota)
                || aliquota < 0 || aliquota > 100))) {
                msgAlerta("IBS/CBS", `As alíquotas informadas devem estar entre 0 e 100%, no item ${indice + 1}.`);
                return false;
            }
        }
        if (!nota.pagamentos.length || nota.pagamentos.some(pagamento => !pagamento.formaPagamento
            || pagamento.valorPagamento < 0 || pagamento.formaPagamento.tipoPagamentoNfe === "99"
            && !pagamento.descricaoPagamento?.trim())) {
            msgAlerta("Pagamentos", "Informe uma forma válida, valor e descrição para a forma Outros.");
            return false;
        }
        const semPagamento = nota.pagamentos.every(pagamento => pagamento.formaPagamento?.tipoPagamentoNfe === "90"
            && pagamento.valorPagamento === 0);
        if (!semPagamento && totalPagamentos !== totalNota) {
            msgAlerta("Pagamentos", "A soma dos pagamentos deve ser igual ao total da nota.");
            return false;
        }
        if (pagamentoAVista && nota.parcelas.length > 0) {
            msgAlerta("Cobrança", "Pagamento à vista não permite parcelas da cobrança.");
            return false;
        }
        if (nota.parcelas.length && arredondar(nota.parcelas.reduce((v, p) => v + p.valorParcela, 0))
            !== (nota.valorLiquidoFatura ?? totalNota)) {
            msgAlerta("Cobrança", "A soma das parcelas deve corresponder ao valor líquido da fatura/nota.");
            return false;
        }
        if (nota.parcelas.some(parcela => !parcela.numeroParcela || !dataValida(parcela.dataVencimento) || parcela.valorParcela <= 0)) {
            msgAlerta("Cobrança", "Informe número, vencimento e valor positivo em cada parcela.");
            return false;
        }
        for (let indice = 1; indice < nota.parcelas.length; indice++) {
            if (nota.parcelas[indice].dataVencimento < nota.parcelas[indice - 1].dataVencimento) {
                msgAlerta("Cobrança", `O vencimento da parcela ${indice + 1} não pode ser anterior ao da parcela ${indice}.`);
                return false;
            }
        }
        if (nota.notasReferenciadas.some(referencia => !/^\d{44}$/.test(referencia.chaveAcesso))) {
            msgAlerta("Referências", "Cada NF-e referenciada precisa de uma chave de acesso com 44 dígitos.");
            return false;
        }
        return true;
    }

    const permiteReenvio = (status: string) => ["REJEITADA", "FALHA_PREPARACAO"].includes(status);
    const podeEnviar = !resultado || permiteReenvio(resultado.status) || resultado.status === "ENVIO_INDETERMINADO";
    const reenvio = edicao || !!resultado && permiteReenvio(resultado.status);

    async function emitir() {
        if (enviando || carregandoParametrosFiscais || erroParametrosFiscais || !podeEnviar || !validar()) return;
        setEnviando(true);
        setErroEmissao(null);
        try {
            const resposta = reenvio
                ? await service.reenviar(edicao ? Number(id) : resultado!.idNotaFiscalSaida, nota)
                : await service.emitir(nota);
            setResultado(resposta);
            if (resposta.status === "AGUARDANDO_SEFAZ") msgSucesso("Emissão enviada", resposta.mensagem);
            else msgAlerta("Envio não concluído", resposta.mensagem);
        } catch (error: unknown) {
            const resposta = isAxiosError(error) ? error.response : undefined;
            const mensagem = mensagemErroApi(resposta?.data);
            if (resposta && ((resposta.status >= 400 && resposta.status < 500) || resposta.status === 503)) {
                const detalhe = mensagem || `Não foi possível enviar a NF-e (HTTP ${resposta.status}).`;
                setErroEmissao(detalhe);
                msgErro("Falha ao enviar NF-e", detalhe);
                return;
            }
            // Falhas sem resposta podem ocorrer depois de o servidor criar a nota; não reenviar automaticamente.
            const detalhe = mensagem
                ? `${mensagem} Verifique a nota no servidor antes de tentar novamente.`
                : "Não foi possível confirmar o resultado. Verifique a nota no servidor antes de tentar novamente.";
            msgErro("Resultado desconhecido", detalhe);
            setResultado({
                idNotaFiscalSaida: 0, notaasInvoiceId: null,
                status: "ENVIO_INDETERMINADO", mensagem: detalhe
            });
        } finally {
            setEnviando(false);
        }
    }

    async function consultarSituacao() {
        if (!resultado?.idNotaFiscalSaida || consultando) return;
        setConsultando(true);
        try {
            const resposta = await service.consultarSituacao(resultado.idNotaFiscalSaida);
            setSituacao(resposta);
            setResultado(anterior => anterior ? { ...anterior, status: resposta.status, mensagem: resposta.motivoSefaz || "Consulta agendada; acompanhe a nota local." } : anterior);
            if (resposta.status === "AUTORIZADA") msgSucesso("NF-e autorizada", "Situação confirmada na SEFAZ.");
            else if (resposta.status === "AUTORIZADA_ESTOQUE_PENDENTE")
                msgAlerta("NF-e autorizada", "A baixa do estoque ainda está pendente.");
            else msgAlerta("Situação da NF-e", resposta.motivoSefaz || resposta.status);
        } catch {
            msgErro("Consulta da NF-e", "Não foi possível consultar a situação fiscal. Tente novamente mais tarde.");
        } finally {
            setConsultando(false);
        }
    }

    function confirmar() {
        if (enviando || !podeEnviar || !validar()) return;
        confirmDialog({
            header: reenvio ? "Confirmar reenvio" : "Confirmar emissão",
            message: `${reenvio ? "Reenviar" : "Enviar"} NF-e de ${moeda(totalNota)} para ${nota.destinatario?.nome} no ambiente ${nota.serieNotaFiscal?.ambiente}?`,
            icon: "pi pi-exclamation-triangle", acceptLabel: reenvio ? "Reenviar NF-e" : "Emitir NF-e", rejectLabel: "Voltar",
            accept: () => { void emitir(); }
        });
    }

    if (carregando) return <div className="p-3">Carregando cadastros da emissão...</div>;
    if (erroCarregamento) return <div className="p-3"><p>{erroCarregamento}</p><Button label="Voltar" icon="pi pi-arrow-left" onClick={() => navigate("/notaFiscalSaida")} /></div>;
    if (!opcoes) return <div className="p-3">Não foi possível abrir a emissão. Recarregue a página.</div>;

    return <div className="p-3">
        <ConfirmDialog />
        <div className="d-flex justify-content-between align-items-center mb-3">
            <h2>{edicao ? `Edição da nota fiscal de saída ${notaOrigem?.numeroNota ?? id}` : "Emissão de nota fiscal de saída"}</h2>
            {!edicao && <Button type="button" label="Documentação" icon="pi pi-book" outlined
                onClick={() => window.open('https://empxdocs.appsotecnologia.com.br/vendas/emissaoNotaFiscal', '_blank', 'noopener,noreferrer')} />}
            {edicao && <Button label="Voltar à consulta" icon="pi pi-arrow-left" outlined disabled={enviando}
                onClick={() => navigate(`/notaFiscalSaida/consultar/${id}`)} />}
        </div>
        {duplicacao && !resultado && <div className="alert alert-info">
            Dados copiados da nota fiscal de saída {idDuplicacao}. Revise os campos, a data de emissão e os vencimentos antes de emitir.
            A emissão criará uma nova nota fiscal, com numeração e chave de acesso próprias, sem vínculo com o pedido original.
        </div>}
        {edicao && notaOrigem && !resultado && <div className="alert alert-warning">
            <strong>{notaOrigem.status.replaceAll("_", " ")}</strong>
            {notaOrigem.motivoSefaz && <div>{notaOrigem.motivoSefaz}</div>}
            <div>A correção preserva a numeração e o histórico fiscal da nota.</div>
            {!nota.enderecoDestinatario && <div>Selecione um endereço ativo para o destinatário.</div>}
        </div>}
        {idPedido && <p className="alert alert-info">Faturamento do pedido {idPedido}. Remova os itens que não deseja faturar e ajuste as quantidades para emissão parcial. Também é possível adicionar itens avulsos à nota. O saldo dos itens do pedido será conferido novamente no envio.</p>}
        <p className="text-muted">Os impostos serão determinados pelos cadastros fiscais e calculados pelo Empx. Após validar o XML, a nota será processada na fila da SEFAZ.</p>
        {erroEmissao && <div className="alert alert-danger" role="alert">
            <strong>Falha ao enviar NF-e</strong><div>{erroEmissao}</div>
        </div>}
        {resultado && <div className={`alert ${resultado.status === "AGUARDANDO_SEFAZ" ? "alert-info" : "alert-warning"}`} role="status">
            <strong>Nota local {resultado.idNotaFiscalSaida || "não confirmada"} — {resultado.status}</strong><br />
            <div>Mensagem: {resultado.mensagem}</div>
            {situacao && <div>
                <strong>Situação atual: {situacao.status}</strong>
                {situacao.numeroNota && <div>Número: {situacao.numeroNota}</div>}
                {situacao.chaveAcesso && <div>Chave de acesso: {situacao.chaveAcesso}</div>}
                {situacao.motivoSefaz && <div>SEFAZ: {situacao.motivoSefaz}</div>}
                {situacao.status.startsWith("AUTORIZADA") && <div>Estoque: {situacao.estoqueMovimentado ? "movimentado" : "sem movimentação concluída"}</div>}
            </div>}
            {resultado.idNotaFiscalSaida > 0 && 
                <Button 
                    label="Abrir nota e histórico" 
                    icon="pi pi-eye" 
                    style={{ marginRight: "0.5rem" }}
                    className="p-button-outlined" onClick={() => navigate(`/notaFiscalSaida/consultar/${resultado.idNotaFiscalSaida}`)} />}
            {resultado.idNotaFiscalSaida > 0 &&
                <Button label={consultando ? "Consultando..." : "Consultar situação"} icon="pi pi-refresh"
                    className="p-button-outlined" disabled={consultando} onClick={() => { void consultarSituacao(); }} />}
            {permiteReenvio(resultado.status)
                ? <div>Corrija os dados indicados e reenvie esta mesma nota.</div>
                : <div>Não envie novamente esta operação sem verificar seu estado fiscal.</div>}
        </div>}
        <fieldset disabled={enviando || !podeEnviar || resultado?.status === "ENVIO_INDETERMINADO"} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
            <TabView>
                <TabPanel header="Dados gerais">
                    {(edicao || duplicacao) && <div className="col-12 mb-3">
                        <Label>Data de emissão</Label>
                        <InputText type="date" value={nota.dataEmissao?.slice(0, 10) ?? ""}
                            onChange={e => alterar({ dataEmissao: e.target.value ? `${e.target.value}T12:00:00-03:00` : null })} className="w-100" />
                    </div>}
                    <div className="col-12 mb-3">
                        <Label obrigatorio>Unidade organizacional</Label>
                        <Dropdown value={nota.unidadeOrganizacional?.id ?? null} options={opcoes.unidades} optionLabel="nome" optionValue="id" filter
                            onChange={e => alterar({ unidadeOrganizacional: opcoes.unidades.find(u => u.id === e.value) ?? null, serieNotaFiscal: null })} className="w-100" />
                    </div>
                    <div className="col-12 mb-3">
                        <Label obrigatorio>Série NF-e (numeração Empx)</Label>
                        <Dropdown value={nota.serieNotaFiscal?.id ?? null} options={opcoes.series.filter(s => s.unidadeOrganizacional?.id === nota.unidadeOrganizacional?.id)}
                            optionLabel="descricao" optionValue="id" filter onChange={e => alterar({ serieNotaFiscal: opcoes.series.find(s => s.id === e.value) ?? null })} className="w-100" />
                    </div>
                    <div className="col-12 mb-3">
                        <Label obrigatorio>Transação de nota fiscal</Label>
                        <Dropdown value={nota.transacaoNotaFiscal?.id ?? null} options={opcoes.transacoes} optionLabel="descricao" optionValue="id" filter
                            onChange={e => {
                                const t = opcoes.transacoes.find(t => t.id === e.value) ?? null;
                                setNota(anterior => ({
                                    ...anterior, transacaoNotaFiscal: t,
                                    naturezaOperacao: t?.naturezaOperacao ?? "", finalidade: t?.finalidadeNfe ?? "NORMAL",
                                    destinoOperacao: t?.destinoOperacao ?? null
                                }));
                            }} className="w-100" />
                    </div>
                    <div className="col-12 mb-3">
                        <Label obrigatorio>Natureza da operação</Label>
                        <InputText value={nota.naturezaOperacao} maxLength={60} onChange={e => alterar({ naturezaOperacao: e.target.value })} className="w-100" />
                    </div>
                    <div className="col-12 mb-3">
                        <Label obrigatorio>Cliente</Label>
                        <Dropdown value={nota.destinatario?.id ?? null} options={opcoes.clientes} optionLabel="nome" optionValue="id" filter
                            onChange={e => { void escolherCliente(opcoes.clientes.find(c => c.id === e.value) ?? null); }} className="w-100" />
                    </div>
                    <div className="col-12 mb-3">
                        <Label obrigatorio>Endereço do destinatário</Label>
                        <Dropdown value={nota.enderecoDestinatario?.id ?? null} options={nota.destinatario?.enderecos?.filter(e => e.ativo) ?? []}
                            optionLabel="logradouro" optionValue="id" onChange={e => alterar({ enderecoDestinatario: nota.destinatario?.enderecos?.find(end => end.id === e.value) ?? null })}
                            className="w-100" />
                        {nota.enderecoDestinatario?.telefone && <div className="mt-2">
                            <strong>Telefone:</strong> {nota.enderecoDestinatario.telefone}
                        </div>}
                    </div>
                    <div className="col-12 mb-3">
                        <Label>Representante</Label>
                        <Dropdown value={nota.representante?.id ?? null} options={opcoes.representantes} optionLabel="nome" optionValue="id" showClear filter
                            onChange={e => alterar({ representante: opcoes.representantes.find(r => r.id === e.value) ?? null })} className="w-100" />
                    </div>
                    {nota.representante && <div className="col-12 mb-3 text-muted">
                        Comissão padrão: {nota.representante.percentualComissaoPadrao}% (calculada por item no servidor).
                    </div>}
                    <div className="col-12 mb-3">
                        <Label>Finalidade</Label>
                        <Dropdown value={nota.finalidade} options={[{ label: "Normal", value: "NORMAL" }, { label: "Complementar", value: "COMPLEMENTAR" },
                        { label: "Ajuste", value: "AJUSTE" }, { label: "Devolução", value: "DEVOLUCAO" }]}
                            onChange={e => alterar({ finalidade: e.value })} className="w-100" />
                    </div>
                    <div className="col-12 mb-3">
                        <Label>Presença do comprador</Label>
                        <Dropdown value={nota.presencaComprador} options={[{ label: "Presencial", value: "PRESENCIAL" }, { label: "Internet", value: "INTERNET" },
                        { label: "Teleatendimento", value: "TELEATENDIMENTO" }, { label: "Presencial fora do estabelecimento", value: "PRESENCIAL_FORA_ESTABELECIMENTO" },
                        { label: "Não presencial — outros", value: "NAO_PRESENCIAL_OUTROS" }]}
                            onChange={e => alterar({ presencaComprador: e.value })} className="w-100" />
                    </div>
                    <div className="col-12 mb-3">
                        <Label>Consumidor final</Label>
                        <Dropdown value={nota.consumidorFinal} options={[{ label: "Não", value: "NAO" }, { label: "Sim", value: "SIM" }]}
                            onChange={e => alterar({ consumidorFinal: e.value })} className="w-100" />
                    </div>
                    <div className="col-12 mb-3">
                        <Label>Intermediador</Label>
                        <Dropdown value={nota.indicadorIntermediador} options={[{ label: "Sem intermediador", value: "SEM_INTERMEDIADOR" }, { label: "Marketplace", value: "COM_INTERMEDIADOR" }]}
                            showClear onChange={e => alterar({ indicadorIntermediador: e.value })} className="w-100" />
                    </div>
                </TabPanel>
                <TabPanel header="Transporte e complementos">
                    <div className="col-12 mb-3">
                        <Label>Modalidade do frete</Label>
                        <Dropdown value={nota.modalidadeFrete} options={[
                            { label: "Por conta do remetente (CIF)", value: "POR_CONTA_REMETENTE" },
                            { label: "Por conta do destinatário (FOB)", value: "POR_CONTA_DESTINATARIO" },
                            { label: "Por conta de terceiros", value: "POR_CONTA_TERCEIROS" },
                            { label: "Transporte próprio do remetente", value: "TRANSPORTE_PROPRIO_REMETENTE" },
                            { label: "Transporte próprio do destinatário", value: "TRANSPORTE_PROPRIO_DESTINATARIO" },
                            { label: "Sem transporte", value: "SEM_TRANSPORTE" }
                        ]} onChange={e => alterar({ modalidadeFrete: e.value })} className="w-100" />
                    </div>
                    <h5>Volumes</h5>
                    {nota.volumes.map((volume, indice) => <div className="card p-3 mb-3" key={indice}>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <strong>Volume {indice + 1}</strong>
                            <Button icon="pi pi-trash" className="p-button-danger p-button-text" aria-label="Remover volume"
                                onClick={() => alterar({ volumes: nota.volumes.filter((_, posicao) => posicao !== indice) })} />
                        </div>
                        <div className="col-12 mb-3">
                            <Label>Quantidade de volumes</Label>
                            <InputNumber value={volume.quantidadeVolumes} min={1} locale="pt-BR"
                                onValueChange={e => alterar({
                                    volumes: nota.volumes.map((v, posicao) => posicao === indice
                                        ? { ...v, quantidadeVolumes: e.value ?? 1 } : v)
                                })} className="w-100" />
                        </div>
                        {([["especie", "Espécie"], ["marca", "Marca"], ["numeracao", "Numeração"]] as const).map(([campo, titulo]) =>
                            <div className="col-12 mb-3" key={campo}>
                                <Label>{titulo}</Label>
                                <InputText value={volume[campo]} maxLength={60} onChange={e => alterar({
                                    volumes: nota.volumes.map((v, posicao) => posicao === indice
                                        ? { ...v, [campo]: e.target.value } : v)
                                })} className="w-100" />
                            </div>)}
                        <div className="col-12 mb-3">
                            <Label>Peso líquido (kg)</Label>
                            <InputNumber value={volume.pesoLiquido} min={0} maxFractionDigits={3} locale="pt-BR"
                                onValueChange={e => alterar({
                                    volumes: nota.volumes.map((v, posicao) => posicao === indice
                                        ? { ...v, pesoLiquido: e.value ?? null } : v)
                                })} className="w-100" />
                        </div>
                        <div className="col-12 mb-3">
                            <Label>Peso bruto (kg)</Label>
                            <InputNumber value={volume.pesoBruto} min={0} maxFractionDigits={3} locale="pt-BR"
                                onValueChange={e => alterar({
                                    volumes: nota.volumes.map((v, posicao) => posicao === indice
                                        ? { ...v, pesoBruto: e.value ?? null } : v)
                                })} className="w-100" />
                        </div>
                    </div>)}
                    <Button label="Adicionar volume" icon="pi pi-plus" className="p-button-outlined mb-3"
                        onClick={() => alterar({
                            volumes: [...nota.volumes,
                            { quantidadeVolumes: 1, especie: "", marca: "", numeracao: "", pesoLiquido: null, pesoBruto: null }]
                        })} />
                    <h5>NF-e referenciadas</h5>
                    {nota.notasReferenciadas.map((referencia, indice) => <div className="col-12 mb-3" key={indice}>
                        <Label>Chave de acesso (44 dígitos)</Label>
                        <div className="d-flex gap-2"><InputText value={referencia.chaveAcesso} maxLength={44}
                            onChange={e => alterar({
                                notasReferenciadas: nota.notasReferenciadas.map((r, posicao) => posicao === indice
                                    ? { ...r, chaveAcesso: e.target.value.replace(/\D/g, "") } : r)
                            })} className="w-100" />
                            <Button icon="pi pi-trash" className="p-button-danger p-button-text" aria-label="Remover referência"
                                onClick={() => alterar({ notasReferenciadas: nota.notasReferenciadas.filter((_, posicao) => posicao !== indice) })} /></div>
                    </div>)}
                    <Button label="Adicionar NF-e referenciada" icon="pi pi-plus" className="p-button-outlined mb-3"
                        onClick={() => alterar({ notasReferenciadas: [...nota.notasReferenciadas, { tipoReferencia: "NFE", chaveAcesso: "" }] })} />
                    <div className="col-12 mb-3">
                        <Label>Informações complementares</Label>
                        <InputTextarea value={nota.informacoesComplementares} rows={5}
                            onChange={e => alterar({ informacoesComplementares: e.target.value })} className="w-100" />
                    </div>
                </TabPanel>
                <TabPanel header="Itens">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5>Itens da nota</h5>
                        <Button label="Adicionar item" icon="pi pi-plus" onClick={() => setItemEmEdicao({ item: novoItem(), indice: null })} />
                    </div>
                    <DataTable value={nota.itens} emptyMessage="Nenhum item adicionado" responsiveLayout="scroll" stripedRows>
                        <Column header="Item" body={(_: ItemEmissaoNotaFiscalSaida, { rowIndex }) => rowIndex + 1} />
                        <Column field="produto.codigo" header="Produto" />
                        <Column field="descricaoProduto" header="Descrição" />
                        <Column field="unidadeComercial" header="Unidade" />
                        <Column field="quantidadeComercial" header="Quantidade" />
                        <Column header="Valor unitário" body={(item: ItemEmissaoNotaFiscalSaida) => moeda(item.valorUnitario)} />
                        <Column header="Transação" body={(item: ItemEmissaoNotaFiscalSaida) =>
                            item.transacaoNotaFiscal?.descricao ?? "Usar transação da nota"} />
                        <Column field="cfop" header="CFOP" />
                        <Column header="IPI" body={(item: ItemEmissaoNotaFiscalSaida) => moeda(calculoService.calcularIpi(item, nota.transacaoNotaFiscal))} />
                        <Column header="Total" body={(item: ItemEmissaoNotaFiscalSaida) => moeda(totalItem(item))} />
                        <Column header="Ações" body={(item: ItemEmissaoNotaFiscalSaida, { rowIndex }) => <div className="d-flex gap-2">
                            <Button icon="pi pi-pencil" aria-label={`Editar item ${rowIndex + 1}`} className="p-button-text"
                                onClick={() => setItemEmEdicao({ item: { ...item }, indice: rowIndex })} />
                            <Button icon="pi pi-trash" aria-label={`Remover item ${rowIndex + 1}`} className="p-button-danger p-button-text"
                                onClick={() => {
                                    valoresInformados.current = new Map(Array.from(valoresInformados.current.entries())
                                        .filter(([posicao]) => posicao !== rowIndex)
                                        .map(([posicao, valores]) => [posicao > rowIndex ? posicao - 1 : posicao, valores]));
                                    alterar({ itens: nota.itens.filter((_, indice) => indice !== rowIndex) });
                                }} />
                        </div>} />
                    </DataTable>
                    <div className="text-end mt-3"><strong>Total dos itens: {moeda(totalNota)}</strong></div>
                    {itemEmEdicao && <NotaFiscalSaidaItemModal nota={nota} item={itemEmEdicao.item}
                        indice={itemEmEdicao.indice ?? nota.itens.length} novo={itemEmEdicao.indice === null}
                        opcoes={opcoes} cfops={cfops} onHide={() => setItemEmEdicao(null)} onSave={item => {
                            const indice = itemEmEdicao.indice ?? nota.itens.length;
                            valoresInformados.current.set(indice, itemService.valoresInformados(item));
                            contextosItens.current.set(indice, itemService.contextoFiscal(item, nota));
                            setNota(anterior => ({ ...anterior, itens: itemEmEdicao.indice === null
                                ? [...anterior.itens, item] : anterior.itens.map((atual, posicao) => posicao === indice ? item : atual) }));
                            setItemEmEdicao(null);
                        }} />}
                </TabPanel>
                <TabPanel header="Pagamento e cobrança">
                    <div className="col-12 mb-3">
                        <Label>Condição de pagamento</Label>
                        <Dropdown value={nota.condicaoPagamento?.id ?? null} options={opcoes.condicoesPagamento} optionLabel="descricao" optionValue="id" filter showClear
                            onChange={e => {
                                const condicaoPagamento = opcoes.condicoesPagamento.find(condicao => condicao.id === e.value) ?? null;
                                alterar({ condicaoPagamento, ...(calculoService.pagamentoAVista(condicaoPagamento) ? {
                                    parcelas: [], numeroFatura: null, valorOriginalFatura: null,
                                    valorDescontoFatura: null, valorLiquidoFatura: null
                                } : {}) });
                            }} className="w-100" />
                    </div>
                    <Button label="Gerar pagamentos e parcelas pela condição" icon="pi pi-refresh" className="p-button-outlined mb-3"
                        disabled={!nota.condicaoPagamento} onClick={gerarCobranca} />
                    {nota.pagamentos.map((pagamento, indice) => <div className="card p-3 mb-3" key={indice}>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5>Pagamento {indice + 1}</h5>
                            <Button icon="pi pi-trash" className="p-button-danger p-button-text" aria-label="Remover pagamento"
                                onClick={() => alterar({ pagamentos: nota.pagamentos.filter((_, posicao) => posicao !== indice) })} />
                        </div>
                        <div className="col-12 mb-3">
                            <Label obrigatorio>Forma de pagamento</Label>
                            <Dropdown value={pagamento.formaPagamento?.id ?? null} options={opcoes.formasPagamento} optionLabel="descricao" optionValue="id" filter
                                onChange={e => alterarPagamento(indice, { formaPagamento: opcoes.formasPagamento.find(f => f.id === e.value) ?? null })} className="w-100" />
                        </div>
                        <div className="col-12 mb-3">
                            <Label obrigatorio>Valor</Label>
                            <InputNumber value={pagamento.valorPagamento} mode="currency" currency="BRL" locale="pt-BR" min={0}
                                onValueChange={e => alterarPagamento(indice, { valorPagamento: e.value ?? 0 })} className="w-100" />
                        </div>
                        {(pagamento.formaPagamento?.tipoPagamentoNfe === "99" || duplicacao) && <div className="col-12 mb-3">
                            <Label obrigatorio={pagamento.formaPagamento?.tipoPagamentoNfe === "99"}>Descrição do pagamento</Label>
                            <InputText value={pagamento.descricaoPagamento ?? ""} maxLength={60}
                                onChange={e => alterarPagamento(indice, { descricaoPagamento: e.target.value || null })} className="w-100" />
                        </div>}
                        {duplicacao && <>
                            <div className="col-12 mb-3">
                                <Label>Integração do pagamento</Label>
                                <Dropdown value={pagamento.tipoIntegracao ?? null} showClear
                                    options={[{ label: "Integrado", value: "INTEGRADO" }, { label: "Não integrado", value: "NAO_INTEGRADO" }]}
                                    onChange={e => alterarPagamento(indice, { tipoIntegracao: e.value ?? null })} className="w-100" />
                            </div>
                            {([["bandeira", "Bandeira", 2], ["autorizacao", "Autorização do pagamento", 20],
                                ["cnpjCredenciadora", "CNPJ da credenciadora", 14]] as const).map(([campo, titulo, limite]) =>
                                <div className="col-12 mb-3" key={campo}>
                                    <Label>{titulo}</Label>
                                    <InputText value={pagamento[campo] ?? ""} maxLength={limite}
                                        onChange={e => alterarPagamento(indice, { [campo]: e.target.value || null })} className="w-100" />
                                </div>)}
                        </>}
                    </div>)}
                    <Button label="Adicionar pagamento" icon="pi pi-plus" className="p-button-outlined mb-3"
                        onClick={() => alterar({ pagamentos: [...nota.pagamentos, novoPagamento()] })} />
                    <div className="col-12 mb-3">
                        <Label>Número da fatura</Label>
                        <InputText value={nota.numeroFatura ?? ""} maxLength={60}
                            placeholder="Se não informado, será usado o número da nota na emissão"
                            onChange={e => alterar({ numeroFatura: e.target.value || null })} className="w-100" />
                    </div>
                    <div className="col-12 mb-3">
                        <Label>Valor original da fatura</Label>
                        <InputNumber value={nota.valorOriginalFatura} mode="currency" currency="BRL" locale="pt-BR" min={0}
                            onValueChange={e => alterar({ valorOriginalFatura: e.value })} className="w-100" />
                    </div>
                    <div className="col-12 mb-3">
                        <Label>Desconto da fatura</Label>
                        <InputNumber value={nota.valorDescontoFatura} mode="currency" currency="BRL" locale="pt-BR" min={0}
                            onValueChange={e => alterar({ valorDescontoFatura: e.value })} className="w-100" />
                    </div>
                    <div className="col-12 mb-3">
                        <Label>Valor líquido da fatura</Label>
                        <InputNumber value={nota.valorLiquidoFatura} mode="currency" currency="BRL" locale="pt-BR" min={0}
                            onValueChange={e => alterar({ valorLiquidoFatura: e.value })} className="w-100" />
                    </div>
                    <h5>Parcelas da cobrança</h5>
                    {pagamentoAVista && <p>Pagamento à vista não permite parcelas da cobrança.</p>}
                    <DataTable value={nota.parcelas} emptyMessage="Nenhuma parcela adicionada" responsiveLayout="scroll" className="mb-3">
                        <Column field="numeroParcela" header="Número"
                            body={(parcela: ParcelaEmissaoNotaFiscalSaida, { rowIndex }) => (
                                <InputText value={parcela.numeroParcela} maxLength={3} aria-label={`Número da parcela ${rowIndex + 1}`}
                                    onChange={e => alterarParcela(rowIndex, { numeroParcela: e.target.value })} className="w-100" />
                            )} />
                        <Column field="dataVencimento" header="Vencimento"
                            body={(parcela: ParcelaEmissaoNotaFiscalSaida, { rowIndex }) => (
                                <InputText type="date" value={parcela.dataVencimento} aria-label={`Vencimento da parcela ${rowIndex + 1}`}
                                    onChange={e => alterarParcela(rowIndex, { dataVencimento: e.target.value })} className="w-100" />
                            )} />
                        <Column field="valorParcela" header="Valor"
                            body={(parcela: ParcelaEmissaoNotaFiscalSaida, { rowIndex }) => (
                                <InputNumber value={parcela.valorParcela} mode="currency" currency="BRL" locale="pt-BR" min={0}
                                    aria-label={`Valor da parcela ${rowIndex + 1}`}
                                    onValueChange={e => alterarParcela(rowIndex, { valorParcela: e.value ?? 0 })} className="w-100" />
                            )} />
                        <Column header="Ações" body={(_: ParcelaEmissaoNotaFiscalSaida, { rowIndex }) => (
                            <Button icon="pi pi-trash" className="p-button-danger p-button-text" aria-label={`Remover parcela ${rowIndex + 1}`}
                                onClick={() => alterar({ parcelas: nota.parcelas.filter((_, posicao) => posicao !== rowIndex) })} />
                        )} />
                    </DataTable>
                    <Button label="Adicionar parcela" icon="pi pi-plus" className="p-button-outlined"
                        disabled={pagamentoAVista}
                        onClick={() => alterar({
                            parcelas: [...nota.parcelas,
                            { numeroParcela: String(nota.parcelas.length + 1).padStart(3, "0"), dataVencimento: "", valorParcela: 0 }]
                        })} />
                </TabPanel>
            </TabView>
        </fieldset>
        <div className="card p-3 mt-3">
            {carregandoParametrosFiscais && <small>Selecionando parâmetros fiscais...</small>}
            <strong>Total da nota: {moeda(totalNota)}</strong>
            <span>Pagamentos: {moeda(totalPagamentos)}</span>
            <small className="text-muted">A transmissão será disponibilizada nas próximas etapas da integração direta com a SEFAZ.</small>
            <Button label={resultado?.status === "ENVIO_INDETERMINADO" ? "Verificar resultado da requisição" : enviando ? "Enviando..." : edicao ? "Salvar e reenviar" : reenvio ? "Reenviar NF-e" : "Emitir NF-e"}
                icon="pi pi-send" className="mt-3"
                disabled={!!itemEmEdicao || enviando || carregandoParametrosFiscais || erroParametrosFiscais || !podeEnviar} onClick={confirmar} />
        </div>
    </div>;
}
